/**
 * Matchmaking API functions
 * 
 * This file contains all API calls related to the matchmaking feature.
 * It provides functions for fetching clients, creating searches, fetching results,
 * managing history, and submitting feedback.
 * 
 * All functions use the centralized apiClient for consistent error handling
 * and authentication.
 */

import { apiClient } from '@/core/api/client';
import { isGenericSearch } from '../constants/client-selection';
import type {
  AdvisorClient,
  BackendAdvisorClient,
  BackendClientsResponse,
  ClientsResponse,
  SearchRequest,
  SearchResponse,
  FilteredProvidersRequest,
  FilteredProvidersResponse,
  SearchResultsRequest,
  SearchResultsResponse,
  SaveResultsRequest,
  HistoryRequest,
  HistoryResponse,
  UserProfile,
  ProviderBase,
  ProviderMedia,
  ProviderFeedbackRequest,
} from './matchmakingApi.types';

// Re-export types for convenience
export type {
  AdvisorClient,
  ClientsResponse,
  SearchRequest,
  SearchResponse,
  FilteredProvidersRequest,
  FilteredProvidersResponse,
  SearchResultsRequest,
  SearchResultsResponse,
  SaveResultsRequest,
  HistoryRequest,
  HistoryResponse,
  UserProfile,
  ProviderBase,
  ProviderMedia,
  ProviderFeedbackRequest,
};

/**
 * Fetches the list of clients associated with the current professional user
 *
 * Feature 036: Uses matchmaking-specific endpoint that supports both FA and SP
 * The /professionals/matchmaking/clients endpoint handles both roles and routes
 * to the appropriate data source based on the authenticated user.
 *
 * @param userRole - The role of the authenticated user (optional, endpoint handles routing)
 * @returns Promise resolving to clients response with array of clients
 * @throws Error if API request fails
 */
export const fetchClients = async (_userRole?: string): Promise<ClientsResponse> => {
  // Use unified professionals endpoint that handles both FA and SP roles
  // Note: _userRole kept for backward compatibility but endpoint handles routing internally
  const endpoint = '/professionals/matchmaking/clients';

  const response = await apiClient.get<BackendClientsResponse>(endpoint);

  // Transform backend client format (client_uuid) to frontend format (id)
  const transformedClients: AdvisorClient[] = (response.data.clients || []).map(
    (client: BackendAdvisorClient) => ({
      ...client,
      id: client.client_uuid,
    })
  );

  return {
    status: response.data.status || 'SUCCESS',
    message: response.data.message || 'Clients fetched successfully',
    clients: transformedClients,
  };
};

/**
 * Creates a new matchmaking search in the backend
 *
 * FEATURE 025: Routes based on userType:
 * - Care Seeker: Routes to /professionals/matchmaking/generic-search (same as FA generic)
 * - Financial Advisor (generic): Routes to /professionals/matchmaking/generic-search
 * - Financial Advisor (client-specific): Routes to /professionals/matchmaking/search
 *
 * This initiates a search with the provided criteria and returns a search ID
 * that can be used to fetch results and filtered providers.
 *
 * @param params - Search request parameters including filters, prompt, and context
 * @returns Promise resolving to search response with searchId and providersFound count
 * @throws Error if search creation fails
 */
export const createSearch = async (params: SearchRequest): Promise<SearchResponse> => {
  // FEATURE 025: Route Care Seeker searches to generic matchmaking endpoint (clientId removed)
  if (params.userType === 'CARE_SEEKER') {
    const { userType, clientId, ...rest } = params;
    const response = await apiClient.post<SearchResponse>('/professionals/matchmaking/generic-search', rest);
    return response.data;
  }

  // Financial Advisor routing (existing logic)
  const isGeneric = isGenericSearch(params.clientId);

  // Choose endpoint based on search type
  const endpoint = isGeneric
    ? '/professionals/matchmaking/generic-search'      // Generic search endpoint
    : '/professionals/matchmaking/search';             // Client-specific endpoint

  // For generic searches, completely remove clientId from payload
  let payload: any;
  if (isGeneric) {
    const { clientId, userType, ...rest } = params;
    payload = rest;
  } else {
    const { userType, ...rest } = params;
    payload = rest;
  }

  const response = await apiClient.post<SearchResponse>(endpoint, payload);
  return response.data;
};

/**
 * Fetches the list of provider IDs that match the search criteria
 *
 * This is used to get filtered provider IDs before calling the Python
 * matchmaking API, ensuring only relevant providers are considered.
 *
 * FEATURE 021: Converts GENERIC_SEARCH_ID to null for backend compatibility.
 *
 * @param params - Request parameters with searchId, advisorId, and clientId
 * @returns Promise resolving to filtered providers response
 * @throws Error if fetch fails
 */
export const fetchFilteredProviders = async (
  params: FilteredProvidersRequest
): Promise<FilteredProvidersResponse> => {
  // Convert GENERIC_SEARCH_ID to null for backend
  const apiParams = {
    ...params,
    clientId: isGenericSearch(params.clientId) ? null : params.clientId,
  };

  const response = await apiClient.post<FilteredProvidersResponse>(
    '/professionals/matchmaking/search/filtered-providers',
    apiParams
  );
  return response.data;
};

/**
 * Fetches paginated search results for a given search
 *
 * Supports pagination via recordStart and recordEnd parameters.
 * Handles both successful responses and failed status responses.
 *
 * FEATURE 021: Converts GENERIC_SEARCH_ID to null for backend compatibility.
 *
 * @param params - Request parameters including searchId and pagination info
 * @returns Promise resolving to search results with providers array
 * @throws Error if fetch fails or status is FAILED
 */
export const fetchSearchResults = async (
  params: SearchResultsRequest
): Promise<SearchResultsResponse> => {
  try {
    // Convert GENERIC_SEARCH_ID to null for backend
    const apiParams = {
      ...params,
      clientId: isGenericSearch(params.clientId) ? null : params.clientId,
    };

    const response = await apiClient.post<SearchResultsResponse>(
      '/professionals/matchmaking/search/results',
      apiParams
    );

    // Check if response indicates failure even with 200 status
    if (response.data.status && response.data.status.toUpperCase() === 'FAILED') {
      throw new Error(response.data.message || 'Failed to fetch search results');
    }

    return response.data;
  } catch (error: any) {
    // If it's an axios error with response data, extract the message
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    // If it's already an Error, re-throw it
    if (error instanceof Error) {
      throw error;
    }
    // Otherwise, throw a generic error
    throw new Error(error.message || 'Failed to fetch search results');
  }
};

/**
 * Saves search results to the database
 *
 * Persists the matchmaking results with scores, AI rationale, and badges
 * for later retrieval in history.
 *
 * FEATURE 021: Converts GENERIC_SEARCH_ID to null for backend compatibility.
 *
 * @param params - Save request with searchId, results array, and metadata
 * @returns Promise that resolves when save is complete
 * @throws Error if save operation fails
 */
export const saveSearchResults = async (params: SaveResultsRequest): Promise<void> => {
  // Convert GENERIC_SEARCH_ID to null for backend
  const apiParams = {
    ...params,
    clientId: isGenericSearch(params.clientId) ? null : params.clientId,
  };

  await apiClient.post('/professionals/matchmaking/search/save-results', apiParams);
};

/**
 * Fetches the search history for a specific advisor-client pair or generic searches
 *
 * FEATURE 021: Routes to different endpoints based on whether fetching generic history:
 * - Generic history (clientId === GENERIC_SEARCH_ID): Routes to /professionals/matchmaking/generic-search/history
 * - Client-specific history: Routes to /professionals/matchmaking/search/history
 *
 * Returns all past searches with their prompts, results counts, and timestamps.
 * Used to display history in the history popover component.
 *
 * @param params - Request parameters with advisorId and clientId
 * @returns Promise resolving to history response with array of history items
 * @throws Error if fetch fails
 */
export const fetchSearchHistory = async (
  params: HistoryRequest
): Promise<HistoryResponse> => {
  const isGeneric = isGenericSearch(params.clientId);


  // Choose endpoint based on search type
  const endpoint = isGeneric
    ? '/professionals/matchmaking/generic-search/history'  // New generic history endpoint
    : '/professionals/matchmaking/search/history';         // Existing client-specific endpoint

  // For generic searches, omit clientId from payload
  const payload = isGeneric
    ? { advisorId: params.advisorId }                 // Only advisorId for generic history
    : params;                                          // Keep both for client-specific history

  const response = await apiClient.post<HistoryResponse>(endpoint, payload);
  return response.data;
};

/**
 * Fetches user profile information by user ID
 * 
 * Used to get user details including logo/headshot for display.
 * 
 * @param userId - The ID of the user to fetch
 * @returns Promise resolving to user profile data
 * @throws Error if fetch fails
 */
export const fetchUserProfile = async (userId: string): Promise<UserProfile> => {
  const response = await apiClient.get<UserProfile>(`/users/${userId}`);
  return response.data;
};

/**
 * Fetches the profile of the currently authenticated user
 * 
 * Used to get advisor profile information including ID and avatar.
 * 
 * @returns Promise resolving to current user's profile data
 * @throws Error if fetch fails
 */
export const fetchCurrentUserProfile = async (): Promise<UserProfile> => {
  const response = await apiClient.get<UserProfile>('/users/profile/me');
  return response.data;
};

/**
 * Fetches base provider information by provider ID
 * 
 * Returns basic provider data including hero media URL.
 * Used for image preloading and profile display.
 * 
 * @param providerId - The ID of the provider to fetch
 * @returns Promise resolving to provider base data
 * @throws Error if fetch fails
 */
export const fetchProviderBase = async (providerId: string): Promise<ProviderBase> => {
  const response = await apiClient.get<ProviderBase>(`/providers/${providerId}/base`);
  return response.data;
};

/**
 * Fetches media (images) associated with a provider
 * 
 * Returns array of media items including URLs and display order.
 * Used to display provider gallery in expanded profile view.
 * 
 * @param providerId - The ID of the provider
 * @returns Promise resolving to provider media data
 * @throws Error if fetch fails
 */
export const fetchProviderMedia = async (providerId: string): Promise<ProviderMedia> => {
  const response = await apiClient.get<ProviderMedia>(`/providers/${providerId}/media`);
  return response.data;
};

/**
 * Submits user feedback for a provider in a search result
 *
 * Allows users to like (heart) or dislike providers to improve
 * future matchmaking recommendations.
 *
 * FEATURE 021: Converts GENERIC_SEARCH_ID to null for backend compatibility.
 *
 * @param params - Feedback request with searchId, providerId, and feedback type
 * @returns Promise that resolves when feedback is saved
 * @throws Error if submission fails
 */
export const submitProviderFeedback = async (params: ProviderFeedbackRequest): Promise<void> => {
  // Convert GENERIC_SEARCH_ID to null for backend
  const apiParams = {
    ...params,
    clientId: isGenericSearch(params.clientId) ? null : params.clientId,
  };

  await apiClient.post('/professionals/matchmaking/search/provider-feedback', apiParams);
};

